import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import SecurityIcon from "@mui/icons-material/Security";
import { ConnectorsAPI } from "../api/connectors";
import PremiumFeatureGate from "../components/PremiumFeatureGate";

const INITIAL_FORM = {
  name: "",
  connector_type: "wmi",
  port: "",
  username: "",
  secret: "",
  auth_mode: "password",
  target_scope: "",
  notes: "",
  is_active: true,
};

function statusColor(status) {
  const value = String(status || "").toLowerCase();
  if (value === "reachable") return { bg: "#dcfce7", fg: "#166534", label: "Port reachable" };
  if (value === "unreachable") return { bg: "#fee2e2", fg: "#991b1b", label: "Port unreachable" };
  return { bg: "#e2e8f0", fg: "#475569", label: "Not tested" };
}

export default function ConnectorVaultPage({ embedded = false }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(INITIAL_FORM);
  const [testTargets, setTestTargets] = useState({});
  const [showForm, setShowForm] = useState(!embedded);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await ConnectorsAPI.list();
      setItems(res?.data?.data || []);
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to load connectors");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const connectorTypes = useMemo(() => ([
    { value: "wmi", label: "WMI / Windows" },
    { value: "ssh", label: "SSH / Linux" },
    { value: "snmp", label: "SNMP / Printer" },
  ]), []);

  const selectedType = form.connector_type;
  const isWmi = selectedType === "wmi";
  const isSsh = selectedType === "ssh";
  const isSnmp = selectedType === "snmp";

  const save = useCallback(async () => {
    if (!form.name.trim()) return setError("Connector name is required");
    if (!form.secret.trim()) return setError("Credential secret is required");
    setSaving(true);
    setError("");
    try {
      await ConnectorsAPI.create({
        ...form,
        port: form.port ? Number(form.port) : undefined,
      });
      setForm(INITIAL_FORM);
      if (embedded) setShowForm(false);
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to save connector");
    } finally {
      setSaving(false);
    }
  }, [form, load, embedded]);

  const runTest = useCallback(async (item) => {
    const deviceIp = String(testTargets[item.id] || "").trim();
    if (!deviceIp) {
      setError("Enter a device IP for the port check.");
      return;
    }
    setTestingId(item.id);
    setError("");
    try {
      await ConnectorsAPI.test(item.id, {
        device_ip: deviceIp,
        port: item.port || undefined,
      });
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to test connector");
    } finally {
      setTestingId(null);
    }
  }, [load, testTargets]);

  const remove = useCallback(async (item) => {
    if (!window.confirm(`Delete connector "${item.name}"?`)) return;
    setError("");
    try {
      await ConnectorsAPI.remove(item.id);
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to delete connector");
    }
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ py: 8, display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  const content = (
    <Box sx={{ px: embedded ? 0 : { xs: 1.5, md: 2.5 }, py: embedded ? 1 : { xs: 1.5, md: 2.5 } }}>
      <Paper sx={{ p: 2.5, border: "1px solid #dbe6f3", borderRadius: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 900 }}>
              Stored credentials
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 760 }}>
              These saved credentials are not used by the current agentless scanner. The available check only tests TCP port reachability from the backend server; it does not verify a username, password, or device inventory.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            {embedded && !showForm && <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setShowForm(true)}>Add credential</Button>}
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => void load()}>Refresh</Button>
          </Stack>
        </Stack>
        {error ? <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert> : null}
      </Paper>

      <Grid container spacing={2}>
        {showForm && <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, borderRadius: 2, border: "1px solid #e2e8f0" }}>
            <Stack spacing={1.5}>
              <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                Add connector
              </Typography>
              <TextField label="Name" value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} />
              <FormControl size="small">
                <InputLabel>Connector type</InputLabel>
                <Select
                  label="Connector type"
                  value={form.connector_type}
                  onChange={(e) => setForm((prev) => ({
                    ...prev,
                    connector_type: e.target.value,
                    auth_mode: e.target.value === "snmp" ? "community" : e.target.value === "ssh" ? "password" : "password",
                    username: e.target.value === "snmp" ? "" : prev.username,
                  }))}
                >
                  {connectorTypes.map((type) => <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.is_active}
                    onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))}
                  />
                }
                label={form.is_active ? "Active" : "Disabled"}
              />
              <TextField label="Port override" type="number" value={form.port} onChange={(e) => setForm((prev) => ({ ...prev, port: e.target.value }))} helperText="Leave blank to use the default for WMI, SSH, or SNMP." />
              {!isSnmp ? (
                <TextField
                  label={isWmi ? "Windows username" : "SSH username"}
                  value={form.username}
                  onChange={(e) => setForm((prev) => ({ ...prev, username: e.target.value }))}
                  helperText={isWmi ? "Use a local or domain account with WMI access." : isSsh ? "Use a login that can SSH into the device." : "SNMP does not need a username for v2c."}
                />
              ) : null}
              <FormControl size="small">
                <InputLabel>Auth mode</InputLabel>
                <Select
                  label="Auth mode"
                  value={form.auth_mode}
                  onChange={(e) => setForm((prev) => ({ ...prev, auth_mode: e.target.value }))}
                  disabled={isWmi || isSnmp}
                >
                  {isSnmp ? (
                    <MenuItem value="community">Community</MenuItem>
                  ) : isWmi ? (
                    <MenuItem value="password">Password</MenuItem>
                  ) : (
                    <>
                      <MenuItem value="password">Password</MenuItem>
                      <MenuItem value="private_key">Private key</MenuItem>
                    </>
                  )}
                </Select>
              </FormControl>
              <TextField
                label={isSnmp ? "SNMP community" : isWmi ? "Windows password" : "SSH password or key"}
                type="password"
                value={form.secret}
                onChange={(e) => setForm((prev) => ({ ...prev, secret: e.target.value }))}
                helperText={isSnmp ? "Use the community string or later SNMPv3 credential." : isWmi ? "Use the account password." : "Use a password or private key passphrase."}
              />
              <TextField label="Target scope" value={form.target_scope} onChange={(e) => setForm((prev) => ({ ...prev, target_scope: e.target.value }))} helperText="Optional subnet, host list, or naming scope. Example: 192.168.0.0/24." />
              <TextField label="Notes" multiline minRows={2} value={form.notes} onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))} helperText="Optional admin note, such as purpose or ownership." />
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => void save()} disabled={saving}>
                {saving ? "Saving..." : "Save connector"}
              </Button>
              {embedded && <Button onClick={() => { setShowForm(false); setForm(INITIAL_FORM); setError(""); }} disabled={saving}>Cancel</Button>}
            </Stack>
          </Paper>
        </Grid>}

        <Grid item xs={12} md={showForm ? 8 : 12}>
          <Stack spacing={1.5}>
            {items.length === 0 ? (
              <Paper sx={{ p: 3, borderRadius: 2, border: "1px solid #e2e8f0" }}>
                <Typography sx={{ fontWeight: 800 }}>No saved credentials.</Typography>
                <Typography variant="body2" color="text.secondary">Network discovery works without a saved credential. Save one here only when preparing for a supported authenticated connector.</Typography>
              </Paper>
            ) : items.map((item) => {
              const badge = statusColor(item.last_test_status);
              return (
                <Paper key={item.id} sx={{ p: 2, borderRadius: 2, border: "1px solid #e2e8f0" }}>
                  <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2}>
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <SecurityIcon fontSize="small" color="action" />
                        <Typography variant="h6" sx={{ fontWeight: 900 }}>{item.name}</Typography>
                        <Chip size="small" label={connectorTypes.find((type) => type.value === item.connector_type)?.label || item.connector_type} />
                        <Chip size="small" label={item.is_active ? "Active" : "Disabled"} sx={{ bgcolor: item.is_active ? "#dcfce7" : "#e2e8f0", color: item.is_active ? "#166534" : "#475569", fontWeight: 700 }} />
                        <Chip size="small" label={badge.label} sx={{ bgcolor: badge.bg, color: badge.fg, fontWeight: 700 }} />
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {item.port ? `Port ${item.port}` : "Default port"} {item.target_scope ? `• Scope ${item.target_scope}` : ""}
                      </Typography>
                      {item.metadata?.notes ? (
                        <Typography variant="body2" color="text.secondary">
                          {item.metadata.notes}
                        </Typography>
                      ) : null}
                      <Typography variant="body2" color="text.secondary">
                        Last test: {item.last_test_at ? new Date(item.last_test_at).toLocaleString() : "Never"}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <FormControlLabel
                        control={
                          <Switch
                            checked={Boolean(item.is_active)}
                            onChange={async (e) => {
                              setError("");
                              try {
                                await ConnectorsAPI.update(item.id, { is_active: e.target.checked });
                                await load();
                              } catch (err) {
                                setError(err?.response?.data?.error || "Failed to update connector");
                              }
                            }}
                          />
                        }
                        label={item.is_active ? "Active" : "Disabled"}
                        sx={{ mr: 1 }}
                      />
                      <TextField
                        size="small"
                        label="Port check IP"
                        placeholder="192.168.0.240"
                        value={testTargets[item.id] || ""}
                        onChange={(e) => setTestTargets((prev) => ({ ...prev, [item.id]: e.target.value }))}
                        sx={{ width: 160 }}
                      />
                      <Button size="small" variant="outlined" startIcon={<CheckIcon />} onClick={() => void runTest(item)} disabled={testingId === item.id || item.connector_type === "snmp"}>
                        {testingId === item.id ? "Checking..." : "Check port"}
                      </Button>
                      <Button size="small" color="inherit" startIcon={<DeleteIcon />} onClick={() => void remove(item)}>
                        Remove
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2, borderRadius: 2, border: "1px solid #dbe6f3", mt: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>Connector scope</Typography>
        <Typography variant="body2" color="text.secondary">
          Current checks do not verify the saved username or secret. SNMP port checking is unavailable because SNMP uses UDP. Credentialed inventory requires a local collector with protocol-specific authentication.
        </Typography>
      </Paper>
    </Box>
  );

  if (embedded) return content;
  return (
    <PremiumFeatureGate
      title="Stored credentials"
      description="Manage saved credentials. Current agentless scans do not use them."
    >
      {content}
    </PremiumFeatureGate>
  );
}
